// src/features/ACS-Dashboard-Modules/Invoice/TabContent/MyInvoicesTab.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Cookies from "js-cookie";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { invoiceApi } from "../InvoiceApi";
import { useSelector, useDispatch } from "react-redux";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { usePrivilege } from "../../../../guard/usePrivilege";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { Eye, Check, X } from "lucide-react";
import { getRole, PERMS } from "../../../../../src/features/ACS-Dashboard-Modules/Invoice/Roles.js";
import { after } from "lodash";
import { addTab, setListViewState } from "../../../../redux/slices/invoiceSlice";
import { fallbackName } from "../Utils/util";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";


/** Default status quick-filter when no saved view is active (matches InvoiceDetails). */
const DEFAULT_QUICK_FILTER = "Pending";

const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Invoice Number",
  "Invoice Audit Status",
  "Invoice Status",
  "Payment Status", 
  "Invoice Date",
  "Invoice Total Charges",
  "Shipment Total Charges", 
  "Total Discrepancy",
  "Payment Terms", 
  "Pay To",
  "Bill To"
];


// Columns mapped to their backend field names.
// "Invoice Audit Status", "Shipment Total Charges", and "Total Discrepancy"
// are sorted in-memory server-side after a full result fetch.
const SORTABLE_COLUMNS = {
  "Invoice Number":          "invoiceNumber",
  "Invoice Date":            "invoiceDate",
  "Invoice Status":          "invoiceStatus",
  "Payment Status":          "paymentStatus",
  "Invoice Total Charges":   "total",
  "Shipment Total Charges":  "shipmentTotal",
  "Total Discrepancy":       "totalDiscrepancy",
  "Invoice Audit Status":    "auditStatus",
  "Payment Terms":           "paymentTerms",
  "Pay To":                  "payToCompanyName",
  "Bill To":                 "billToCompanyName",
};

const statusBadgeClass = {
  DRAFT:      "bg-orange-200 dark:bg-orange-700/60 text-gray-800 dark:text-gray-200",
  RECALLED:   "bg-neutral-200 dark:bg-neutral-700/40 text-gray-800 dark:text-gray-200",
  PROCESSING: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  APPROVED:   "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  REJECTED:   "bg-red-300 dark:bg-red-900/40 text-black dark:text-red-200",
  PENDING:    "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  DEFAULT:    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

const auditBadgeClass = {
  PASSED:     "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  FAILED:     "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  PENDING:    "bg-yellow-100 dark:bg-yellow-900/40 text-black dark:text-yellow-200",
  DEFAULT:    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

const paymentBadgeClass = {
  UNPAID:           "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  "PARTIALLY PAID": "bg-yellow-100 dark:bg-yellow-900/40 text-black dark:text-yellow-200",
  PAID:             "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  OVERPAID:         "bg-purple-100 dark:bg-purple-900/40 text-black dark:text-purple-200",
  REFUNDED:         "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  DEFAULT:          "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};


const TYPE_MAP = {
  "Base Charge":    "COST",
  "Buy Rate":       "BUY_RATE",
  "Sell Rate":      "SELL_RATE",
  "Revenue Charge": "REVENUE",
};

function getCurrentUser() {
  try {
    const token = Cookies.get("authToken");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.username || payload.email || payload.sub;
  } catch {
    return null;
  }
}

function normalizeType(raw) {
  return TYPE_MAP[raw] || "COST";  
}

const statusCls = (v) =>
  statusBadgeClass[v?.toUpperCase()] || statusBadgeClass.DEFAULT;

const auditCls = (v) =>
  auditBadgeClass[v?.toUpperCase()] || auditBadgeClass.DEFAULT;


export default function MyInvoicesTab({
  searchQuery = "",
  statusFilter = null,
  onUpdateCount = () => {},
  onInvoiceClick = () => {},
  onInvoiceReject = () => {},
  onInvoiceCreate = () => {},
  onApplyStatusFilter,
  stateKey = "my-invoices",
}) {
  const formatMoney = (amount, currency) => {
    if (amount == null || amount === "") return null;
    const n = Number(amount);
    if (Number.isNaN(n)) return null;
    const safe = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const code = /^[A-Z]{3}$/.test(safe) ? safe : "USD";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${code}`;
    }
  };

  const auditRefreshKey = useSelector(s => s.invoices.auditRefreshKey);
  const listViewState = useSelector(s => s.invoices.listViewState || {});
  const savedView = listViewState[stateKey] || {};
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonAction, setReasonAction] = useState(null);
  const [reasonNote, setReasonNote] = useState("");
  const [pendingInv, setPendingInv] = useState(null);

  const toggleInvoiceSelection = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const [invoices,   setInvoices]   = useState([]);
  const [page,       setPage]       = useState(savedView.currentPage || 1);
  const [size,       setSize]       = useState(savedView.itemsPerPage || 50);
  const [sortBy,     setSortBy]     = useState(savedView.sortBy ?? "invoiceDate");
  const [ascending,  setAscending]  = useState(savedView.ascending ?? false);
  const [totalPages, setTotalPages] = useState(0);
  const [openMenu,   setOpenMenu]   = useState(null);
  const menuRef                     = useRef(null);
  const [showBulkMenu,    setShowBulkMenu]    = useState(false);
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef       = useRef(null);
  const { hasPrivilege } = usePrivilege();
  const canApprove = hasPrivilege("invoices:APPROVE");
  const canReject  = hasPrivilege("invoices:REJECT");
  const canRecall  = hasPrivilege("invoices:RECALL");
  const canCreate  = hasPrivilege("invoices:CREATE");
  const dispatch   = useDispatch();

  const getExtraConfig = useCallback(
    () => ({ currentPage: page, pageSize: size, filter: statusFilter }),
    [page, size, statusFilter]
  );

  const handleApplySavedViewConfig = useCallback(
    (config, _viewMeta, options = {}) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setSize(config.pageSize);
      if (config.filter !== undefined) {
        onApplyStatusFilter?.(config.filter);
      } else if (options.resetExtraConfig) {
        onApplyStatusFilter?.(DEFAULT_QUICK_FILTER);
      }
    },
    [onApplyStatusFilter]
  );

  const tableViews = useTableSavedViews({
    pageKey: stateKey || TABLE_PAGE_KEYS.INVOICE_MY,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Invoice Number"],
    getExtraConfig,
    onApplyConfig: handleApplySavedViewConfig,
  });

  const {
    columnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  const prevQuickFilterForDirtyRef = useRef(statusFilter);

  // Reconcile dirty when the user changes the quick filter, not when activeView updates after save.
  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== statusFilter;
    prevQuickFilterForDirtyRef.current = statusFilter;
    if (!quickFilterChanged) return;

    reconcileDirtyState();
  }, [activeView, statusFilter, viewsLoading, reconcileDirtyState]);

  // ✅ Portal dropdown position state
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const [isLoading, setIsLoading] = useState(true);

  const [officeCode] = useState(() => Cookies.get("officeCodes"));
  const role  = getRole(officeCode);
  const perms = PERMS[role];
  const API   = process.env.REACT_APP_API_BASE_URL + "/api";

  useEffect(() => {
    dispatch(
      setListViewState({
        stateKey,
        currentPage: page,
        itemsPerPage: size,
        sortBy,
        ascending,
      })
    );
  }, [dispatch, stateKey, page, size, sortBy, ascending]);

  const handleSort = (newSortBy) => {
    setPage(1);
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  useEffect(() => {
    let cancel = false;
    setIsLoading(true);

    (async () => {
      try {
        const resp = await invoiceApi.detailedSearch(
          { query: searchQuery, invoiceStatus: statusFilter || "", sortBy, ascending },
          page - 1,
          size
        );
        if (cancel) return;
        onUpdateCount(resp.totalElements);
        setTotalPages(resp.totalPages);

        console.log("raw pageInvoices:", resp.content);

        if (!cancel) {
          setInvoices(resp.content);
        }
      } catch (err) {
        console.error(err);
        if (!cancel) {
          setInvoices([]);
          onUpdateCount(0);
          setTotalPages(0);
        }
      } finally {
        if (!cancel) setIsLoading(false);
      }
    })();

    return () => { cancel = true; };
  }, [searchQuery, statusFilter, page, size, sortBy, ascending, officeCode, auditRefreshKey, onUpdateCount]);

  useEffect(() => {
    const handler = e => {
      if (
        bulkMenuRef.current &&
        !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current &&
        !checkboxHeaderRef.current.contains(e.target)
      ) {
        setShowBulkMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-action-trigger]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleRecall = async inv => {
    try {
      const token = Cookies.get("authToken");
      const officeHeader = inv.billToOfficeCode;

      const { data: full } = await axios.get(
        `${API}/invoices/${inv.uniqueId}`,
        { headers: { "Office-Code": officeHeader, "Authorization": `Bearer ${token}` } }
      );

      await axios.put(
        `${API}/invoices/${inv.invoiceNumber}/recall`,
        {},
        { headers: { "Office-Code": officeHeader, "Authorization": `Bearer ${token}` } }
      );

      if (full.shipments && full.shipments.length > 0) {
        await Promise.all(
          full.shipments.map(s =>
            axios.put(
              `${API}/shipments/${s.shipmentNumber}`,
              { isInvoiced: false, invoiceNumber: "--", invoiceStatus: "--", invoiceDate: "" },
              { headers: { "Office-Code": officeHeader, "Authorization": `Bearer ${token}` } }
            )
          )
        );
      }

      setInvoices(list => list.filter(i => i.invoiceNumber !== inv.invoiceNumber));

      notify.success({ title: "Invoice recalled", message: "Invoice moved back to Drafts." });

    } catch (e) {
      console.error("❌ Recall failed:", e?.response?.data || e);
      let errorMessage = "Could not recall the invoice";
      if (e?.response?.data?.content)      errorMessage = e.response.data.content;
      else if (e?.response?.data?.message) errorMessage = e.response.data.message;
      else if (e?.message)                 errorMessage = e.message;
      notify.error({ title: "Recall failed", message: errorMessage });
    } finally {
      setOpenMenu(null);
    }
  };

  const handleReasonModalSubmit = async () => {
    if (!reasonNote.trim()) {
      notify.warning({ title: "Required", message: "Please provide a reason" });
      return;
    }

    try {
      if (reasonAction === "recall") {
        await handleRecall(pendingInv);
      } else if (reasonAction === "reject") {
        if (window.rejectableIds && window.rejectableIds.length > 1) {
          for (const id of window.rejectableIds) {
            await handleReject(id);
          }
          notify.warning({
            title: "Bulk rejection complete",
            message: `${window.rejectableIds.length} invoices rejected.`
          });
          setSelectedInvoices([]);
          setShowBulkMenu(false);
          window.rejectableIds = null;
        } else {
          await handleReject(pendingInv.uniqueId);
        }
      }

      await axios.put(
        `${API}/invoices/${pendingInv.uniqueId}/note`,
        { note: reasonNote },
        {
          headers: {
            "Authorization": `Bearer ${Cookies.get("authToken")}`,
            "Office-Code": pendingInv.billToOfficeCode
          }
        }
      );

      setShowReasonModal(false);
      setReasonNote("");
    } catch (e) {
      console.error("Error:", e);
      notify.error({
        title: "Operation failed",
        message: e?.response?.data?.message || "Could not complete action"
      });
    }
  };

  function chargeKey(c) {
    return [c.shipmentNumber, c.code, normalizeType(c.type), c.version].join("|");
  }

  async function handleApprove(uniqueId, suppressNotification = false) {
    try {
      const inv = invoices.find(i => i.uniqueId === uniqueId);
      if (!inv) throw new Error("Invoice not found in local state");

      const invoiceNumber  = inv.invoiceNumber;
      const officeHeader   = inv.billToOfficeCode;
      const token          = Cookies.get("authToken");

      const updatedInv = await axios.put(
        `${API}/invoices/update-invoice/${uniqueId}`,
        {
          ...inv,
          invoiceStatus: "Approved",
          approvalDate:  new Date().toISOString(),
          approvedBy:    getCurrentUser(),
          rejectedDate:  null,
          rejectedBy:    null
        },
        {
          headers: {
            "Office-Code": inv.payToOfficeCode || inv.billToOfficeCode,
            Authorization: `Bearer ${Cookies.get("authToken")}`
          }
        }
      );

      setInvoices(list =>
        list.map(i => (i.uniqueId === uniqueId ? {
          ...updatedInv.data,
          auditStatus:    i.auditStatus,
          charges:        i.charges,
          shipmentTotal:  i.shipmentTotal
        } : i))
      );

      if (!suppressNotification) {
        notify.success({
          title: "Invoice approved",
          message: `Invoice ${updatedInv.data.invoiceNumber} approved successfully.`
        });
      }

      return updatedInv.data.invoiceNumber;

    } catch (err) {
      console.error("❌ Approve failed:", err);
      let errorMessage = "See console for details";
      if (err?.response?.data?.content)      errorMessage = err.response.data.content;
      else if (err?.response?.data?.message) errorMessage = err.response.data.message;
      else if (err?.message)                 errorMessage = err.message;
      if (!suppressNotification) {
        notify.error({ title: "Approval failed", message: errorMessage });
      }
      throw err;
    } finally {
      setOpenMenu(null);
    }
  }

  async function handleReject(id) {
    try {
      const row = invoices.find(i => i.uniqueId === id);
      if (!row) throw new Error("Invoice not found in local state");

      const officeHeader = row.payToOfficeCode;
      const token        = Cookies.get("authToken");

      const { data: full } = await axios.get(
        `${API}/invoices/${id}`,
        { headers: { "Office-Code": officeHeader, "Authorization": `Bearer ${token}` } }
      );

      if (!full.shipments || full.shipments.length === 0) {
        throw new Error("No shipments found in invoice");
      }

      const shipments = await Promise.all(
        full.shipments.map(s =>
          axios.get(`${API}/shipments/detailed-shipment/${s.shipmentNumber}`, {
            headers: { "Office-Code": row.billToOfficeCode, "Authorization": `Bearer ${token}` }
          }).then(r => r.data)
        )
      );

      console.log(`✅ Successfully fetched ${shipments.length} shipments`);

      await Promise.all(
        shipments.map(s =>
          axios.put(
            `${API}/shipments/${s.shipmentNumber}`,
            { isInvoiced: false, invoiceNumber: "--", invoiceStatus: "Rejected", invoiceDate: "" },
            { headers: { "Office-Code": row.billToOfficeCode, "Authorization": `Bearer ${token}` } }
          )
        )
      );

      console.log(`✅ Successfully updated ${shipments.length} shipments`);

      const updatedInv = await invoiceApi.updateInvoice(
        id,
        {
          ...full,
          invoiceStatus: "Rejected",
          rejectedDate:  new Date().toISOString(),
          rejectedBy:    getCurrentUser(),
          approvalDate:  null,
          approvedBy:    null
        },
        officeHeader
      );

      console.log(`✅ Invoice ${updatedInv.invoiceNumber} status updated to Rejected`);

      setInvoices(list =>
        list.map(i => i.uniqueId === id ? {
          ...updatedInv,
          auditStatus:   i.auditStatus,
          charges:       i.charges,
          shipmentTotal: i.shipmentTotal
        } : i)
      );

      notify.warning({
        title: "Invoice rejected",
        message: `Invoice ${updatedInv.invoiceNumber} and ${shipments.length} shipment(s) released successfully.`
      });

    } catch (e) {
      console.error("❌ Reject failed:", e?.response?.data || e);
      let errorMessage = "Unknown error occurred";
      if (e?.response?.data?.content)      errorMessage = e.response.data.content;
      else if (e?.response?.data?.message) errorMessage = e.response.data.message;
      else if (e?.message)                 errorMessage = e.message;
      notify.error({ title: "Reject failed", message: `Failed to reject invoice: ${errorMessage}` });
    } finally {
      setOpenMenu(null);
    }
  }

  const selectedInvoiceObjects = selectedInvoices
    .map(id => invoices.find(i => i.uniqueId === id))
    .filter(Boolean);

  const hasApproveEligible = selectedInvoiceObjects.some(
    inv => inv.invoiceStatus?.toLowerCase() === "pending" && inv.auditStatus?.toLowerCase() === "passed"
  );

  const hasRejectEligible = selectedInvoiceObjects.some(
    inv => inv.invoiceStatus?.toLowerCase() === "pending"
  );

  const rows = invoices.map(inv => {
    const st = (inv.invoiceStatus || "").toLowerCase();
    const au = (inv.auditStatus   || "").toLowerCase();

    console.log("invoice audit status", au);

    const actions = [];

    if (st !== "draft") {
      actions.push({ icon: "Eye", label: "View", color: "blue", action: () => onInvoiceClick(inv) });
    }

    if (st === "draft") {
      actions.push({
        icon: "Edit", label: "Resume", color: "blue",
        action: () => dispatch(addTab({ name: `Edit Draft (${inv.invoiceNumber})`, type: "new", data: inv }))
      });
    } else if (st === "rejected" && canCreate) {
      actions.push({
        icon: "Edit", label: "Resume", color: "blue",
        action: () => dispatch(addTab({ name: `Edit (${inv.invoiceNumber})`, type: "new", data: inv }))
      });
    }

    if (st === "pending") {
      actions.push({
        icon: "Undo2", label: "Recall", color: "orange",
        action: () => { setPendingInv(inv); setReasonAction("recall"); setReasonNote(""); setShowReasonModal(true); }
      });
    }

    if (st === "pending" && canApprove && au === "passed") {
      actions.push({ icon: "Check", label: "Approve", color: "green", action: () => handleApprove(inv.uniqueId) });
    }

    if (st === "pending" && canReject) {
      actions.push({
        icon: "X", label: "Reject", color: "red",
        action: () => { setPendingInv(inv); setReasonAction("reject"); setReasonNote(""); setShowReasonModal(true); }
      });
    }

    return {
      Selection:               inv.uniqueId,
      Action:                  inv,
      "Invoice Number":        inv.invoiceNumber,
      "Invoice Date":          inv.invoiceDate,
      "Payment Terms":         inv.paymentTerms,
      Description:             inv.description,
      "Invoice Total Charges": formatMoney(inv.total, inv.currency),
      "Shipment Total Charges":formatMoney(inv.shipmentTotal, inv.currency),
      Currency:                inv.currency,
      "Pay To":                inv.payToCompanyName,
      "Bill To":               inv.billToCompanyName,
      "Invoice Status": (
        <span className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${statusBadgeClass[(inv.invoiceStatus || "").toUpperCase()] || statusBadgeClass.DEFAULT}`}>
          {(inv.invoiceStatus || "-").toUpperCase()}
        </span>
      ),
      "Payment Status": (
        <span className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${paymentBadgeClass[(inv.paymentStatus || "UNPAID").toUpperCase()] || paymentBadgeClass.DEFAULT}`}>
          {(inv.paymentStatus || "UNPAID").toUpperCase()}
        </span>
      ),
      "Invoice Audit Status": (
        <span className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${auditBadgeClass[(inv.auditStatus || "").toUpperCase()] || auditBadgeClass.DEFAULT}`}>
          {(inv.auditStatus || "-").toUpperCase()}
        </span>
      ),
      "Total Discrepancy": inv.totalDiscrepancy != null
  ? formatMoney(inv.totalDiscrepancy, inv.currency)
  : "—",
      __actions: actions
    };
  });

  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedInvoices([]); return; }
    setSelectedInvoices(rows.map(r => r.Selection));
  };

  // ✅ Capture button position for portal dropdown
  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top:  rect.bottom + window.scrollY,
      left: rect.left   + window.scrollX,
    });
    setOpenMenu(prev => (prev === id ? null : id));
  };

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Invoices...." />
      ) : (
        <>
          {/* Search info banner */}
          {searchQuery && (
            <div className="shrink-0 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
              Showing search results for: <span className="font-semibold">"{searchQuery}"</span>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={viewsError}
              isDirty={isDirty}
              allDataHeaders={TABLE_HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          </div>
          <div className="flex-1 min-h-[200px] bg-white sm:min-h-[300px] overflow-x-auto overflow-y-auto">
            <Table
              {...columnManagerProps}
              data={rows}
              selectedRows={selectedInvoices}
              totalValidRows={rows.length}
              onSelectAll={handleSelectAll}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              dataColumnMinWidth={180}
              dataColumnMaxWidth={400}
              initialColumnWidths={columnWidths}
              onColumnResize={onColumnResize}
              renderCheckboxActions={() => setShowBulkMenu(v => !v)}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id      = row.Selection;
                const actions = row.__actions;
                const isOpen  = openMenu === id;
                const { className: selectionClass, style: selectionStyle } = getStickyCellProps("Selection");
                const { className: actionClass, style: actionStyle } = getStickyCellProps("Action");
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                return (
                  <>
                    {/* Selection checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${selectionClass}`}
                      style={{ ...selectionStyle, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="
                          relative appearance-none cursor-pointer mx-auto block 
                          w-[15px] h-[15px] rounded-sm 
                          border border-gray-400 dark:border-acs-dark-border 
                          bg-white dark:bg-acs-dark-surface 
                          transition-none
                          checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark
                          checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark
                          after:content-[''] after:absolute after:inset-0
                          after:flex after:items-center after:justify-center
                          checked:after:content-['✓']
                          checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selectedInvoices.includes(id)}
                        onChange={() => toggleInvoiceSelection(id)}
                      />
                    </td>

                    {/* ✅ Action button (no ActionMenu inside td) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${actionClass}`}
                      style={{ ...actionStyle, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {/* ✅ Portal dropdown */}
                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{
                          position: "absolute",
                          top:  dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                        }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen={isOpen}
                          onClose={() => setOpenMenu(null)}
                          moduleName="Invoice"
                          actions={actions}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Data cells */}
                    {/* {TABLE_HEADERS.slice(2).map((col, i) => ( */}
                      {dataHeadersToRender.map((col) => {
  const { className: colClass, style: colStyle } = getStickyCellProps(col);

  return (
    <td
      key={col}
      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
      style={{
        ...colStyle,
        whiteSpace:    "nowrap",
        overflow:      "hidden",
        textOverflow:  "ellipsis",
        width:         "200px",
        minWidth:      "200px",
        maxWidth:      "200px",
      }}
      onDoubleClick={() => {
        const inv = row.Action;
        const st  = (inv.invoiceStatus || "").toLowerCase();
        const canCreate = hasPrivilege("invoices:CREATE");

        if (st === "draft") {
          dispatch(addTab({ name: `Edit Draft (${inv.invoiceNumber})`, type: "new", data: inv }));
        } else if (st === "rejected" && canCreate) {
          dispatch(addTab({ name: `Edit (${inv.invoiceNumber})`, type: "new", data: inv }));
        } else {
          onInvoiceClick(inv);
        }
      }}
    >
      {row[col]}
    </td>
  );
})}

                  </>
                );
              }}
            </Table>

            {showBulkMenu && checkboxHeaderRef.current && (
              <div
                ref={bulkMenuRef}
                className="absolute z-[1000]"
                style={{
                  top:  checkboxHeaderRef.current.getBoundingClientRect().bottom + window.scrollY + 6,
                  left: checkboxHeaderRef.current.getBoundingClientRect().left   + window.scrollX,
                }}
              >
                <ActionMenu
                  rowId="bulk-invoices"
                  isOpen
                  onClose={() => setShowBulkMenu(false)}
                  moduleName="Invoice"
                  actions={[
                    ...(canApprove && hasApproveEligible ? [{
                      icon: "Check", label: "Approve Selected", color: "green",
                      action: async () => {
                        const approveableIds = selectedInvoices.filter(id => {
                          const inv = invoices.find(i => i.uniqueId === id);
                          return inv &&
                            inv.invoiceStatus?.toLowerCase() === "pending" &&
                            inv.auditStatus?.toLowerCase() === "passed";
                        });

                        if (approveableIds.length === 0) {
                          notify.warning({ title: "No eligible invoices", message: "Selected invoices must be Pending with Passed audit status." });
                          setShowBulkMenu(false);
                          return;
                        }

                        const approvedInvoiceNumbers = [];
                        for (const id of approveableIds) {
                          try {
                            const invoiceNumber = await handleApprove(id, true);
                            approvedInvoiceNumbers.push(invoiceNumber);
                          } catch (err) {
                            console.error(`Failed to approve invoice ${id}:`, err);
                          }
                        }

                        notify.success({
                          title: "Bulk approval complete",
                          message: `Successfully approved ${approvedInvoiceNumbers.length} invoice${approvedInvoiceNumbers.length > 1 ? "s" : ""}: ${approvedInvoiceNumbers.join(", ")}`
                        });
                        setSelectedInvoices([]);
                        setShowBulkMenu(false);
                      }
                    }] : []),
                    ...(canReject && hasRejectEligible ? [{
                      icon: "X", label: "Reject Selected", color: "red",
                      action: async () => {
                        const rejectableIds = selectedInvoices.filter(id => {
                          const inv = invoices.find(i => i.uniqueId === id);
                          return inv && inv.invoiceStatus?.toLowerCase() === "pending";
                        });

                        if (rejectableIds.length === 0) {
                          notify.warning({ title: "No eligible invoices", message: "Selected invoices must be Pending to reject." });
                          setShowBulkMenu(false);
                          return;
                        }

                        if (rejectableIds.length < selectedInvoices.length) {
                          notify.info({ title: "Partial rejection", message: `Rejecting ${rejectableIds.length} of ${selectedInvoices.length} selected invoices (only Pending).` });
                        }

                        const firstId = rejectableIds[0];
                        setPendingInv(invoices.find(i => i.uniqueId === firstId));
                        setReasonAction("reject");
                        setReasonNote("");
                        setShowReasonModal(true);
                        window.rejectableIds = rejectableIds;
                      }
                    }] : [])
                  ]}
                />
              </div>
            )}
          </div>

          <GlobalPagination
            label="Invoices per page"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={size}
            onItemsPerPageChange={setSize}
          />

          {showReasonModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold mb-2 dark:text-acs-dark-text">
                  {reasonAction === "reject" ? "Reject Invoice" : "Recall Invoice"}
                </h3>
                <p className="text-sm text-gray-600 dark:text-acs-dark-subtext mb-4">Please add a note:</p>
                <textarea
                  value={reasonNote}
                  onChange={(e) => setReasonNote(e.target.value)}
                  placeholder="Enter note..."
                  className="w-full border border-gray-300 rounded px-3 py-2 min-h-[100px] dark:bg-acs-dark-surface dark:border-acs-dark-border dark:text-acs-dark-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => { setShowReasonModal(false); setReasonNote(""); }}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReasonModalSubmit}
                    className={`px-4 py-2 text-white rounded ${reasonAction === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}
                  >
                    {reasonAction === "reject" ? "Reject" : "Recall"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}